import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import { IMAGES } from "@/data/products";

const TAGS = ["All", "Gift Boxes", "Packaging", "Personalised", "Events", "Concierge"];

const ITEMS = [
  { src: IMAGES.heroMain, tag: "Gift Boxes", caption: "The signature ivory edit", ratio: "aspect-[3/4]" },
  { src: IMAGES.packaging, tag: "Packaging", caption: "Tissue, ribbon & wax seal", ratio: "aspect-square" },
  { src: IMAGES.eventTable, tag: "Events", caption: "Table favours for a private dinner", ratio: "aspect-[4/5]" },
  { src: IMAGES.bespoke, tag: "Personalised", caption: "A bespoke edit in progress", ratio: "aspect-[3/4]" },
  { src: IMAGES.founder, tag: "Concierge", caption: "Sourcing, first-hand", ratio: "aspect-[4/5]" },
  { src: IMAGES.boxFlat, tag: "Gift Boxes", caption: "The Golden Hour crate", ratio: "aspect-square" },
  { src: IMAGES.artisanGold, tag: "Personalised", caption: "Gold-foiled detailing", ratio: "aspect-[3/4]" },
  { src: IMAGES.heroSecondary, tag: "Packaging", caption: "Winter wrapping, by hand", ratio: "aspect-[4/5]" },
  { src: IMAGES.seasonal, tag: "Events", caption: "Midsummer table gifting", ratio: "aspect-square" },
  { src: IMAGES.artisanCeramic, tag: "Personalised", caption: "Monogrammed keepsakes", ratio: "aspect-[3/4]" },
  { src: IMAGES.artisanCandle, tag: "Gift Boxes", caption: "The Morning Ritual edit", ratio: "aspect-[4/5]" },
  { src: IMAGES.littleGift, tag: "Packaging", caption: "Little tokens, wrapped", ratio: "aspect-square" },
];

export default function Gallery() {
  const [tag, setTag] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(() => (tag === "All" ? ITEMS : ITEMS.filter((i) => i.tag === tag)), [tag]);

  return (
    <div data-testid="gallery-page">
      <section className="relative pt-40 pb-16 lg:pb-20 spotlight">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp><Eyebrow>Gallery</Eyebrow></FadeUp>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-chocolate leading-[1.08] tracking-tight mt-6">
            <MaskedLine delay={0.1}>The work,</MaskedLine>
            <MaskedLine delay={0.25} className="italic text-golddark">up close.</MaskedLine>
          </h1>
          <FadeUp delay={0.35}>
            <p className="text-base md:text-lg text-chocolate/65 leading-relaxed max-w-2xl mt-7">
              Gift boxes, packaging, personalised pieces, events and concierge commissions — a glimpse inside the studio.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-24 lg:pb-32">
        <FadeUp>
          <div data-testid="gallery-filter" className="flex flex-wrap gap-2.5 border-y border-chocolate/10 py-6">
            {TAGS.map((t) => (
              <button
                key={t}
                data-testid={`gallery-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setTag(t)}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-all duration-300 ${
                  tag === t
                    ? "bg-chocolate text-cream border-chocolate"
                    : "border-chocolate/15 text-chocolate/60 hover:border-gold hover:text-chocolate"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 mt-12 [&>*]:mb-6">
          {items.map((item, i) => (
            <FadeUp key={`${item.src}-${item.tag}`} delay={Math.min(i * 0.05, 0.3)}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(item)}
                className={`group relative block w-full overflow-hidden ${item.ratio} cursor-pointer`}
              >
                <img src={item.src} alt={item.caption} loading="lazy"
                  className="img-warm absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]" />
                <span className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/30 transition-colors duration-500" />
                <span className="absolute inset-x-0 bottom-0 p-5 text-left translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.25em] text-champagne">{item.tag}</span>
                  <span className="block font-serif italic text-lg text-cream mt-1">{item.caption}</span>
                </span>
              </button>
            </FadeUp>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-espresso/85 backdrop-blur-sm" onClick={() => setLightbox(null)} />
            <motion.figure
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-w-3xl w-full"
            >
              <img src={lightbox.src} alt={lightbox.caption} className="img-warm w-full max-h-[75vh] object-cover frame-gold" />
              <figcaption className="flex items-center justify-between mt-4">
                <span className="font-serif italic text-lg text-cream">{lightbox.caption}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-champagne">{lightbox.tag}</span>
              </figcaption>
              <button data-testid="gallery-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close"
                className="absolute -top-4 -right-4 w-10 h-10 bg-cream text-chocolate flex items-center justify-center hover:bg-gold transition-colors">
                <X size={16} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
